"use client";

import { JSX, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { fmtEur, fmtNum } from "@/lib/format";
import { useViewerLanguage } from "@/lib/i18n";
import { useElementWidth } from "@/lib/hooks/use-element-width";
import { useTween } from "@/lib/hooks/use-tween";
import type { ModuleRoiCalculatorData } from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";
import { KEYBOARD_SETTLE_MS } from "@/config/timing";

interface InputSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  format: (value: number) => string;
  controlId: string;
  controlIndex: number;
}

function InputSlider({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  format,
  controlId,
  controlIndex,
}: InputSliderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inputModeRef = useRef<"pointer" | "keyboard">("pointer");
  const pendingRef = useRef<number | null>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const emit = useCallback(
    (committed: number) => {
      emitModuleInteraction(
        rootRef.current,
        "click",
        { kind: "control", id: controlId, index: controlIndex },
        { value: committed },
      );
    },
    [controlId, controlIndex],
  );

  const flushPending = useCallback(() => {
    if (settleRef.current !== null) {
      clearTimeout(settleRef.current);
      settleRef.current = null;
    }
    if (pendingRef.current === null) return;
    const committed = pendingRef.current;
    pendingRef.current = null;
    emit(committed);
  }, [emit]);

  useEffect(
    () => () => {
      if (settleRef.current !== null) clearTimeout(settleRef.current);
    },
    [],
  );

  return (
    <div ref={rootRef} className="block">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-sm text-[var(--muted)]">{label}</span>
        <span className="whitespace-nowrap text-sm font-medium tabular-nums text-[var(--fg)]">
          {format(value)}
        </span>
      </div>
      <Slider
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={([next]) => onChange(next)}
        onPointerDown={() => {
          flushPending();
          inputModeRef.current = "pointer";
        }}
        onKeyDown={() => {
          inputModeRef.current = "keyboard";
        }}
        onValueCommit={([next]) => {
          if (inputModeRef.current === "pointer") {
            emit(next);
            return;
          }
          // Arrow keys commit on every single press, so a pause in typing (or
          // focus leaving the slider) stands in for releasing the pointer.
          pendingRef.current = next;
          if (settleRef.current !== null) clearTimeout(settleRef.current);
          settleRef.current = setTimeout(flushPending, KEYBOARD_SETTLE_MS);
        }}
        onBlur={flushPending}
        className={cn(
          "[&_[data-slot=slider-track]]:h-[3px] [&_[data-slot=slider-track]]:bg-[var(--track)]",
          "[&_[data-slot=slider-range]]:bg-[var(--module-accent)]",
          "[&_[data-slot=slider-thumb]]:size-4 [&_[data-slot=slider-thumb]]:border-[var(--module-accent)]",
          "[&_[data-slot=slider-thumb]]:bg-[var(--card)]",
        )}
      />
    </div>
  );
}

function CompareBar({
  label,
  value,
  max,
  pattern,
  color,
}: {
  label: string;
  value: number;
  max: number;
  pattern?: boolean;
  color?: string;
}) {
  const { intlLocale } = useViewerLanguage();
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-sm text-[var(--muted)]">{label}</span>
        <span className="text-sm font-medium tabular-nums text-[var(--fg)]">
          {fmtEur(value, intlLocale)}
        </span>
      </div>
      <div className="relative h-3 overflow-hidden rounded-[4px] bg-black/[0.04] dark:bg-white/[0.06]">
        <div
          className={cn(
            "absolute inset-y-0 left-0 rounded-[4px]",
            pattern &&
              "handyout-pattern-diag ring-1 ring-inset ring-black/[0.14] dark:ring-white/[0.10]",
          )}
          style={{
            width: `${pct}%`,
            background: pattern ? undefined : color,
            transition: "width 0.45s ease",
          }}
        />
      </div>
    </div>
  );
}

export default function ModuleRoiCalculator({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleRoiCalculatorData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, intlLocale } = useViewerLanguage();
  const initTeam = data.team ?? 25;
  const initPresentations = data.presentations ?? 4;
  const initHoursSaved = data.hoursSaved ?? 0.5;
  const initHourlyRate = data.hourlyRate ?? 75;
  const seatPrice = data.seatPrice ?? 30;

  const [ref, width] = useElementWidth<HTMLDivElement>();
  const compact = width > 0 && width < 600;

  const [team, setTeam] = useState(initTeam);
  const [presentations, setPresentations] = useState(initPresentations);
  const [hoursSaved, setHoursSaved] = useState(initHoursSaved);
  const [hourlyRate, setHourlyRate] = useState(initHourlyRate);

  const hoursYear = team * presentations * hoursSaved * 12;
  const value = hoursYear * hourlyRate;
  const cost = team * seatPrice * 12;
  const net = value - cost;
  const roi = cost > 0 ? (net / cost) * 100 : 0;
  const paybackMonths = value > 0 ? cost / (value / 12) : Number.POSITIVE_INFINITY;
  const netAnimated = useTween(net, { duration: 450 });
  const max = Math.max(value, cost);

  return (
    <div
      ref={ref}
      className={cn(
        "grid w-full gap-6",
        compact ? "grid-cols-1" : "grid-cols-[1fr_1.1fr]",
        className,
      )}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className="space-y-5">
        <InputSlider
          label={t("Team size")}
          value={team}
          min={1}
          max={200}
          onChange={setTeam}
          format={(v) => t("{count} people", { count: v })}
          controlId="team"
          controlIndex={0}
        />
        <InputSlider
          label={t("Presentations per person / month")}
          value={presentations}
          min={1}
          max={20}
          onChange={setPresentations}
          format={(v) => fmtNum(v, 0, intlLocale)}
          controlId="presentations"
          controlIndex={1}
        />
        <InputSlider
          label={t("Follow-up saved per presentation")}
          value={hoursSaved}
          min={0.25}
          max={3}
          step={0.25}
          onChange={setHoursSaved}
          format={(v) =>
            t("{hours} h", { hours: fmtNum(v, Number.isInteger(v) ? 0 : 2, intlLocale) })
          }
          controlId="hoursSaved"
          controlIndex={2}
        />
        <InputSlider
          label={t("Internal hourly rate")}
          value={hourlyRate}
          min={40}
          max={150}
          step={5}
          onChange={setHourlyRate}
          format={(v) => fmtEur(v, intlLocale)}
          controlId="hourlyRate"
          controlIndex={3}
        />
      </div>

      <div
        className={cn(
          "rounded-xl p-5 ring-1 ring-black/[0.1] dark:ring-white/[0.12]",
          !compact && "self-start",
        )}
      >
        <div className="text-sm text-[var(--muted)]">
          {t("Net savings per year")}
        </div>
        <div className="mt-1 text-4xl font-medium tracking-tight tabular-nums">
          {fmtEur(Math.round(netAnimated), intlLocale)}
        </div>
        <div className="mt-1 text-sm text-[var(--subtle)]">
          {t("after license costs")}
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3 border-t border-dashed border-[var(--border-dashed)] pt-4">
          <div>
            <div className="text-xs text-[var(--subtle)]">{t("ROI")}</div>
            <div className="mt-0.5 text-[15px] font-medium tabular-nums">
              {fmtNum(roi, 0, intlLocale)} %
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--subtle)]">{t("Payback after")}</div>
            <div className="mt-0.5 text-[15px] font-medium tabular-nums">
              {paybackMonths === Number.POSITIVE_INFINITY
                ? "–"
                : t("{months} mo", { months: fmtNum(paybackMonths, 1, intlLocale) })}
            </div>
          </div>
          <div>
            <div className="text-xs text-[var(--subtle)]">{t("Time saved")}</div>
            <div className="mt-0.5 text-[15px] font-medium tabular-nums">
              {t("{hours} h", { hours: fmtNum(hoursYear, 0, intlLocale) })}
            </div>
          </div>
        </div>

        <div className="mt-5 space-y-3.5 border-t border-dashed border-[var(--border-dashed)] pt-4">
          <CompareBar
            label={t("Value of time saved")}
            value={value}
            max={max}
            color="var(--module-accent)"
          />
          <CompareBar label={t("License costs")} value={cost} max={max} pattern />
        </div>
      </div>
    </div>
  );
}
